import type { AxiosResponse } from 'axios'
import apiClient from './client'

export interface DiagnosticSummary {
  id: number
  machine_id: string
  collected_at: string
  trigger: string | null
}

export async function listDiagnostics(params: {
  machine_id?: string
  trigger?: string
  days?: number
  limit?: number
}): Promise<DiagnosticSummary[]> {
  const response = await apiClient.get<DiagnosticSummary[]>('/diagnostics/', { params })
  return response.data
}

/**
 * Save a blob response as a downloaded file, honoring the server's
 * Content-Disposition filename when present.
 *
 * A plain window.open(url) would bypass the JWT Authorization header
 * (apiClient only attaches it to XHR/fetch calls, not browser navigations),
 * so these endpoints must be fetched as a blob and saved via an anchor.
 */
function saveJsonBlob(response: AxiosResponse, fallbackName: string): void {
  let filename = fallbackName
  const cd = response.headers['content-disposition'] || response.headers['Content-Disposition']
  if (cd && typeof cd === 'string') {
    const m = /filename\s*=\s*"?([^";]+)"?/i.exec(cd)
    if (m && m[1]) filename = m[1]
  }

  const blob = new Blob([response.data as BlobPart], { type: 'application/json' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

/**
 * Download a single diagnostic as a JSON file via the authenticated apiClient.
 */
export async function downloadDiagnosticFile(id: number, suggestedName?: string): Promise<void> {
  const response = await apiClient.get(`/diagnostics/${id}/download`, {
    responseType: 'blob',
  })
  saveJsonBlob(response, suggestedName || `diagnostic-${id}.json`)
}

/**
 * Export all diagnostics for a machine over N days as a JSON file. Uses the
 * authenticated apiClient (the endpoint requires a JWT).
 */
export async function exportDiagnostics(machineId: string, days = 7): Promise<void> {
  const response = await apiClient.get(`/diagnostics/export/${machineId}`, {
    params: { days },
    responseType: 'blob',
  })
  saveJsonBlob(response, `diagnostics-${machineId}-${days}d.json`)
}
